import { Fragment, useState } from 'react'
import { refMatches } from '../buildAnswer.js'
import { useWidgetContext } from './context.js'
import { Card, CopyButton, GotchaCallout, GotchaChips, SystemChip, asList, systemName } from './primitives.jsx'
import w from './widgets.module.css'

const csvCell = (value) => {
  const text = String(value ?? '')
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

export function mappingCsv(fields) {
  return [
    'source_field,target_field,type,required,note',
    ...fields.map((f) => [f.name, f.target, f.type, Boolean(f.required), f.note].map(csvCell).join(',')),
  ].join('\n')
}

export function mappingJson(fields) {
  return JSON.stringify(
    fields.map((f) => ({
      source: f.name,
      target: f.target ?? null,
      type: f.type ?? null,
      required: Boolean(f.required),
      ...(f.note ? { note: f.note } : {}),
    })),
    null,
    2,
  )
}

/** #4 Mapping grid (show_fields with target_system) or a field table with purposes. */
export function MappingGrid({ input, block }) {
  const { systemsMap } = useWidgetContext()
  const fields = asList(input.fields).filter((f) => f?.name)
  const gotchas = asList(block.gotchas)
  const [openId, setOpenId] = useState(null)
  if (!fields.length) return null

  const mapping = Boolean(input.target_system) || fields.some((f) => f.target)
  const required = fields.filter((f) => f.required).length
  const rowGotchas = (f) => gotchas.filter((g) => refMatches(g.attach_to?.ref, f.name) || refMatches(g.attach_to?.ref, f.target))
  const columns = mapping ? 4 : 3
  const sourceName = systemName(systemsMap, input.source_system)
  const targetName = systemName(systemsMap, input.target_system)

  return (
    <Card
      label={input.title ?? (mapping ? 'Field mapping' : 'Fields')}
      title={input.title ?? (mapping ? 'Field mapping' : 'Fields')}
      sub={
        input.source_system ? (
          <span className={w.rowTight}>
            <SystemChip slug={input.source_system} />
            {input.target_system ? (
              <>
                <span>→</span>
                <SystemChip slug={input.target_system} />
              </>
            ) : null}
          </span>
        ) : null
      }
      right={
        mapping && block.complete ? (
          <>
            <CopyButton text={() => mappingCsv(fields)} label="Copy CSV" />
            <CopyButton text={() => mappingJson(fields)} label="Copy JSON" />
          </>
        ) : null
      }
    >
      <div className={w.scrollX}>
        <table className={w.table}>
          <thead>
            <tr>
              <th scope="col">{mapping ? `${sourceName || 'Source'} field` : 'Field'}</th>
              {mapping ? (
                <>
                  <th aria-hidden="true" />
                  <th scope="col">{`${targetName || 'Target'} field`}</th>
                </>
              ) : null}
              <th scope="col">{mapping ? 'Type' : 'Purpose'}</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f, i) => {
              const own = rowGotchas(f)
              const open = own.find((g) => g.id === openId)
              return (
                <Fragment key={`${f.name}-${i}`}>
                  <tr>
                    <td>
                      <code className={w.mono}>{f.name}</code>
                      {f.required ? (
                        <span className={w.req} title="Required">
                          *
                        </span>
                      ) : null}
                      {!mapping && f.type ? <span className={w.cellNote}>{f.type}</span> : null}
                      {own.length ? (
                        <div style={{ marginTop: 6 }}>
                          <GotchaChips items={own} openId={openId} onToggle={setOpenId} />
                        </div>
                      ) : null}
                    </td>
                    {mapping ? (
                      <>
                        <td className={w.arrowCell} aria-hidden="true">
                          →
                        </td>
                        <td>
                          <code className={w.mono}>{f.target ?? '—'}</code>
                          {f.note ? <span className={w.cellNote}>{f.note}</span> : null}
                        </td>
                        <td className={w.muted}>{f.type ?? ''}</td>
                      </>
                    ) : (
                      <td>
                        {f.purpose}
                        {f.note ? <span className={w.cellNote}>{f.note}</span> : null}
                      </td>
                    )}
                  </tr>
                  {open ? (
                    <tr className={w.fullRow}>
                      <td colSpan={columns}>
                        <GotchaCallout item={open} open onToggle={() => setOpenId(null)} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {required ? (
        <div className={w.muted}>
          <span className={w.req}>*</span> Required · {fields.length} fields, {required} required
        </div>
      ) : (
        <div className={w.muted}>{fields.length} fields</div>
      )}
    </Card>
  )
}
